import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { CheckCircle, Mail, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Emploi = () => {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });

  useEffect(() => { document.title = `${t('emploi.title')} — Les Noces de Ginkgo`; }, [t]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setError('Veuillez remplir tous les champs obligatoires.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/emploi`, form);
      setSubmitted(true);
    } catch {
      setError('Une erreur est survenue. Veuillez réessayer ou nous écrire directement.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="font-sans bg-cream min-h-screen pt-16">

      {/* Header */}
      <section className="bg-ginkgo-900 py-20 md:py-28 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <p className="text-gold text-xs uppercase tracking-[0.3em] font-medium mb-4">
            {t('emploi.subtitle')}
          </p>
          <h1 className="font-serif text-5xl md:text-6xl text-white font-medium">{t('emploi.title')}</h1>
        </motion.div>
      </section>

      <div className="max-w-2xl mx-auto px-4 py-16 md:py-24">

        {/* Intro */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <div className="w-12 h-px bg-gold mb-8" />
          <p className="text-stone-600 text-base leading-relaxed">{t('emploi.intro')}</p>

          <div className="mt-6 flex items-center gap-3 bg-ginkgo-50 border border-ginkgo-200 px-5 py-4">
            <Mail size={16} className="text-ginkgo-700 flex-shrink-0" />
            <div>
              <p className="text-sm text-stone-600">{t('emploi.contact_direct')}</p>
              <a href="mailto:vdetraz@lesnocesdeginkgo.ch" className="text-sm text-ginkgo-800 font-medium hover:text-gold transition-colors">
                vdetraz@lesnocesdeginkgo.ch
              </a>
            </div>
          </div>
        </motion.div>

        {/* Form or Success */}
        {submitted ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="text-center bg-white border border-stone-100 p-10"
            data-testid="emploi-success"
          >
            <CheckCircle size={48} className="text-ginkgo-600 mx-auto mb-6" />
            <h2 className="font-serif text-3xl text-ginkgo-800 mb-4">{t('emploi.success_title')}</h2>
            <p className="text-stone-600 text-base leading-relaxed">{t('emploi.success_text')}</p>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            <h2 className="font-serif text-3xl text-ginkgo-800 mb-8 pb-3 border-b border-stone-200">
              {t('emploi.form_title')}
            </h2>
            <form onSubmit={handleSubmit} data-testid="emploi-form" className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="sm:col-span-2">
                  <label className="block text-xs uppercase tracking-widest text-stone-600 font-medium mb-2">
                    {t('emploi.name_label')}
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder={t('emploi.name_placeholder')}
                    data-testid="emploi-name"
                    className="w-full border border-stone-200 bg-white px-4 h-12 text-sm text-stone-700 focus:outline-none focus:border-ginkgo-500 rounded-none"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-stone-600 font-medium mb-2">
                    {t('emploi.email_label')}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder={t('emploi.email_placeholder')}
                    data-testid="emploi-email"
                    className="w-full border border-stone-200 bg-white px-4 h-12 text-sm text-stone-700 focus:outline-none focus:border-ginkgo-500 rounded-none"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-stone-600 font-medium mb-2">
                    {t('emploi.phone_label')}
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder={t('emploi.phone_placeholder')}
                    data-testid="emploi-phone"
                    className="w-full border border-stone-200 bg-white px-4 h-12 text-sm text-stone-700 focus:outline-none focus:border-ginkgo-500 rounded-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-stone-600 font-medium mb-2">
                  {t('emploi.message_label')}
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={7}
                  placeholder={t('emploi.message_placeholder')}
                  data-testid="emploi-message"
                  className="w-full border border-stone-200 bg-white px-4 py-3 text-sm text-stone-700 focus:outline-none focus:border-ginkgo-500 rounded-none resize-none"
                />
              </div>

              {error && (
                <div data-testid="emploi-error" className="flex items-start gap-3 bg-red-50 border border-red-200 px-4 py-3">
                  <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="emploi-submit"
                className="w-full bg-ginkgo-800 text-white py-4 text-sm uppercase tracking-widest font-medium hover:bg-ginkgo-900 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? t('emploi.submitting') : t('emploi.submit')}
              </button>
            </form>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Emploi;
